import PropTypes from 'prop-types'
import { FormattedMessage, useIntl } from 'react-intl'
import { boolean, object, string } from 'yup'

import Button from '@/components/Button'
import Form, { Checkbox, Select, Text, Textarea, yupTestContextHelper } from '@/components/Form'

import messages from './messages'

import './index.scss'

export const getCustomSchemaUrn = (organizationId: any) =>
  `urn:ietf:params:scim:schemas:extension:workgrid:2.0:${organizationId}`

function UserAttributeForm(props: any) {
  const intl = useIntl()

  const schema = object().shape({
    displayName: string()
      .trim()
      // @ts-expect-error
      .required(<FormattedMessage {...messages.requiredError} />)
      .test(
        'isUnique',
        'This name is already in use. Please try another.',
        yupTestContextHelper(props.testDisplayName)
      ),
    description: string(),
    scimAttributeName: string()
      .trim()
      // @ts-expect-error
      .required(<FormattedMessage {...messages.requiredError} />)
      /*
       * SCIM attribute name definition specified in RFC 7643 section 2.1
       * Validate the first character separately from all subsequent characters to provide more readable and specific
       * errors to the user. Note that while the RFC does not allow $ as the first character of a name, SCIM
       * specification does use them for the "$ref" sub-attribute so validation accounts for this anomaly here.
       */
      // @ts-expect-error
      .matches(/^[A-Za-z]|\$ref/, <FormattedMessage {...messages.scimAttributeNameFirstCharacterError} />)
      // @ts-expect-error
      .matches(/^[A-Za-z][\w$-]*$|\$ref/, <FormattedMessage {...messages.scimAttributeNameNthCharacterError} />)
      .test(
        'isUnique',
        'This name is already in use. Please try another.',
        yupTestContextHelper(props.testSCIMAttributeName)
      ),
    scimParentAttributeName: string().nullable(),
    targetable: boolean().nullable(),
    // @ts-expect-error
    type: string().required(<FormattedMessage {...messages.requiredError} />)
  })

  const types = {
    binary: messages.typeBinary,
    boolean: messages.typeBoolean,
    complex: messages.typeComplex,
    datetime: messages.typeDateTime,
    decimal: messages.typeDecimal,
    integer: messages.typeInteger,
    string: messages.typeString
  }

  const onSubmit = (values: any) => {
    // cast to a boolean
    return props.onSubmit({
      ...values,
      targetable: !props.disabled.includes('targetable') && values.targetable === 'true'
    })
  }

  const { children, disabled } = props
  return (
    <Form
      buttons={
        <>
          <Button color="secondary" onClick={props.onCancel}>
            {intl.formatMessage(messages.cancelButton)}
          </Button>
          {disabled.length < 5 && (
            <Button color="primary" type="submit">
              {intl.formatMessage(props.submitTitle)}
            </Button>
          )}
        </>
      }
      className="user-attribute__form"
      initialValues={props.values}
      onReset={props.onCancel}
      onSubmit={onSubmit}
      schema={schema}
    >
      {children as any}

      <Text
        disabled={disabled.includes('displayName')}
        label={<FormattedMessage {...messages.displayNameLabel} />}
        name="displayName"
        placeholder={intl.formatMessage(messages.displayNamePlaceholder)}
        required
      />

      <Textarea
        disabled={disabled.includes('description')}
        label={<FormattedMessage {...messages.descriptionLabel} />}
        name="description"
        placeholder={intl.formatMessage(messages.descriptionPlaceholder)}
      />

      <Select
        disabled={disabled.includes('type')}
        label={<FormattedMessage {...messages.typeLabel} />}
        name="type"
        required
      >
        {props.types.map((type: any) => (
          <Select.Option key={type} value={type}>
            {/* @ts-expect-error */}
            {intl.formatMessage(types[type])}
          </Select.Option>
        ))}
      </Select>

      <Text
        description={<FormattedMessage {...messages.scimAttributeNameDescription} />}
        disabled={disabled.includes('scimAttributeName')}
        label={<FormattedMessage {...messages.scimAttributeNameLabel} />}
        name="scimAttributeName"
        placeholder={intl.formatMessage(messages.scimAttributeNamePlaceholder)}
        required
      />

      {!disabled.includes('targetable') && (
        <Checkbox.Input
          description={<FormattedMessage key="description" {...messages.targetableDescription} />}
          id="targetable"
          label={<FormattedMessage {...messages.targetableLabel} />}
          name="targetable"
          value="true"
        />
      )}
    </Form>
  )
}

UserAttributeForm.displayName = 'UserAttributeForm'

UserAttributeForm.propTypes = {
  children: PropTypes.node,
  disabled: PropTypes.array,

  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  submitTitle: PropTypes.exact({ defaultMessage: PropTypes.string.isRequired, id: PropTypes.string.isRequired }),
  testDisplayName: PropTypes.func.isRequired,
  testSCIMAttributeName: PropTypes.func.isRequired,
  types: PropTypes.arrayOf(
    PropTypes.oneOf(['binary', 'boolean', 'complex', 'datetime', 'decimal', 'integer', 'string'])
  ),
  values: PropTypes.shape({
    description: PropTypes.string,
    displayName: PropTypes.string,
    scimAttributeName: PropTypes.string,
    scimParentAttributeName: PropTypes.string,
    targetable: PropTypes.string,
    type: PropTypes.oneOf(['binary', 'boolean', 'complex', 'datetime', 'decimal', 'integer', 'string'])
  })
}

UserAttributeForm.defaultProps = {
  children: null,
  disabled: [],
  submitTitle: messages.submitButton,
  types: ['binary', 'boolean', 'complex', 'datetime', 'decimal', 'integer', 'string'],
  values: {
    description: '',
    displayName: '',
    scimAttributeName: '',
    scimParentAttributeName: '',
    targetable: 'false',
    type: 'string'
  }
}

export default UserAttributeForm
